import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import { ConfirmModal } from "global/components/AppModal";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { FeatureState, useFeatureState } from "hooks/use-feature";
import { ContractDocument } from "screens/ContractSearch/ContractSearchTypes";
import { selectId } from "redux/authSlice";
import { useAppSelector } from "redux/hooks";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import CompareButton from "./components/CompareButton";
import CompareNotice, { CompareMessage } from "./components/CompareNotice";
import CompareProgress from "./components/CompareProgress";
import {
  AlertTriangleGlyph,
  ArrowLeftGlyph,
  HourglassGlyph,
  LockGlyph,
  RefreshGlyph,
} from "./components/ContractCompareIcons";
import OldVersionPicker, { OldPick, OldTab } from "./components/OldVersionPicker";
import OutcomePanel, { PanelAction } from "./components/OutcomePanel";
import ResultView from "./components/ResultView";
import UploadZone from "./components/UploadZone";
import styles from "./ContractCompare.styles";
import t from "./ContractCompare.tokens";
import { countdownMessage } from "./ContractCompareHelper";
import {
  CompareFailure,
  CompareInput,
  compareUserKey,
  readCompareRouterState,
  safeFileName,
} from "./ContractCompareTypesAndValidation";
import { useContractCompare } from "./useContractCompare";
import { useContractLibrary } from "./useContractLibrary";
import { useLeaveGuard } from "./useLeaveGuard";

const page = strings.contractCompare.page;

interface UploadState {
  file: File | null;
  /** Why the file was refused; set together with `file`, so the chip can show "not accepted". */
  problem: string | null;
}

const EMPTY_UPLOAD: UploadState = { file: null, problem: null };

const BUTTON_ICON = { width: t.button.icon, height: t.button.icon };
const PANEL_ICON = { width: t.panel.icon, height: t.panel.icon };

/** Failures that replace the picker with a panel; every other failure shows on the picker itself. */
const PANEL_KINDS: CompareFailure["kind"][] = ["limit", "switchedOff", "unavailable", "reload", "timedOut"];

/**
 * **Compare versions** (`/contracts/compare`): pick the old version from the user's own contracts (or upload it),
 * upload the new version, press Compare. Three views of one page -- the pickers, the comparing panel, and the result
 * -- all in component state (a result is never stored: redux persists to localStorage, and a contract is not for the
 * disk). Gated by the AI Assistant plan feature and the `sign` + `fetch` role (D7).
 */
const ContractCompareComponent = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  useTitle(title.CONTRACT_COMPARE);
  useSetPageHeader(isPhone ? page.title : undefined);

  const hasFetchPermission = hasAccessTo(strings.resources.sign, strings.fetchPermission);
  const planState = useFeatureState(FeatureKeys.AI_ASSISTANT);

  // ---- what the user chose
  // The router state that carried a pick from Contract Search counts only for the user it was made for (D58 c).
  const userKey = compareUserKey(useAppSelector(selectId));
  const routerPick = useRef(readCompareRouterState(history.location.state, userKey)).current;
  const [tab, setTab] = useState<OldTab>("library");
  const tabTouched = useRef(!!routerPick);
  const [pick, setPick] = useState<OldPick | null>(routerPick);
  const [pickFromSearch, setPickFromSearch] = useState(!!routerPick);
  const [pickError, setPickError] = useState<string | null>(null);
  const [oldUpload, setOldUpload] = useState<UploadState>(EMPTY_UPLOAD);
  const [newUpload, setNewUpload] = useState<UploadState>(EMPTY_UPLOAD);
  // `handleFeatureError` only refreshes the feature list; until it has, the failure itself says why the page is blocked.
  const [localGate, setLocalGate] = useState<{ gate: "plan" | "admin"; message: string | null } | null>(null);

  const leaveRelease = useRef<() => void>(() => undefined);
  const compare = useContractCompare({ onBeforeSessionExpired: () => leaveRelease.current() });
  // The hook's callbacks are stable; the object it returns is not, so effects depend on these.
  const { state, cancel: cancelCompare, reset: resetCompare } = compare;
  const failure = state.phase === "failed" ? state.failure : null;

  const gateState: FeatureState =
    planState.status !== "available"
      ? planState
      : localGate
        ? localGate.gate === "plan"
          ? { status: "not-in-plan" }
          : { status: "admin-disabled", disabledMessage: localGate.message ?? undefined }
        : { status: "available" };
  const blocked = gateState.status !== "available";
  const disabledMessage =
    gateState.status === "not-in-plan"
      ? page.lockedHint
      : gateState.status === "admin-disabled"
        ? (gateState.disabledMessage ?? strings.contractCompare.switchedOff)
        : undefined;

  // Said once per busy answer to screen readers; the visible countdown ticks without being a live region.
  const busyAnnouncement = useMemo(
    () => (failure?.kind === "busy" ? countdownMessage(failure, compare.secondsLeft) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [failure],
  );

  const library = useContractLibrary(hasFetchPermission && !blocked);

  // ---- leaving the page
  const startOver = useCallback(() => {
    cancelCompare();
    resetCompare();
  }, [cancelCompare, resetCompare]);
  const guard = useLeaveGuard({
    active: state.phase === "running" || state.phase === "result",
    onSameLocation: startOver,
  });
  leaveRelease.current = guard.release;

  // The default tab is chosen once, when the first page of the list is in: the library if it holds a PDF or Word
  // contract, else Upload. It never changes after the user touched the tabs (D46).
  const defaultTabChosen = useRef(false);
  useEffect(() => {
    if (library.status === "loading" || library.status === "skipped" || defaultTabChosen.current) return;
    defaultTabChosen.current = true;
    if (!tabTouched.current && library.status === "ready" && library.contracts.length === 0) setTab("upload");
  }, [library.status, library.contracts.length]);

  const selectTab = (next: OldTab) => {
    tabTouched.current = true;
    setTab(next);
    compare.reset();
  };

  // The router state that carried the pick from Contract Search follows the pick (D50): a refresh keeps a pick made
  // there, and a dropped pick (a 404) is not brought back.
  useEffect(() => {
    const carried = readCompareRouterState(history.location.state, userKey);
    if (!carried) return;
    if (pick && pick.documentId === carried.documentId) return;
    guard.bypass(() =>
      history.replace(
        { pathname: history.location.pathname, search: history.location.search },
        pick ? { documentId: pick.documentId, fileName: pick.fileName, userKey } : undefined,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);

  // ---- the old side
  const onPick = (document: ContractDocument) => {
    setPick({ documentId: document.documentId, fileName: safeFileName(document.filename) });
    setPickFromSearch(false);
    setPickError(null);
    compare.reset();
  };

  const onUploadFile = (side: "old" | "new") => (file: File, problem: string | null) => {
    (side === "old" ? setOldUpload : setNewUpload)({ file, problem });
    compare.reset();
  };
  const onUploadRemove = (side: "old" | "new") => () => {
    (side === "old" ? setOldUpload : setNewUpload)(EMPTY_UPLOAD);
    compare.reset();
  };

  // A refusal the service named a part for lands under that file; the choices stay (D17).
  useEffect(() => {
    if (!failure) return;
    if (failure.kind === "gate") {
      setLocalGate({ gate: failure.gate, message: failure.message });
      return;
    }
    if (failure.kind !== "file") return;
    if (failure.part === "revised") {
      setNewUpload((previous) => ({ ...previous, problem: failure.message }));
    } else if (failure.part === "original") {
      setOldUpload((previous) => ({ ...previous, problem: failure.message }));
    } else {
      setPickError(failure.message);
      if (failure.dropPick) {
        setPick(null);
        setPickFromSearch(false);
      }
    }
    resetCompare();
  }, [failure, resetCompare]);

  // ---- the request
  const oldReady = tab === "library" ? !!pick : !!oldUpload.file && !oldUpload.problem;
  const newReady = !!newUpload.file && !newUpload.problem;
  const input: CompareInput | null =
    oldReady && newReady && newUpload.file
      ? {
          revised: newUpload.file,
          old:
            tab === "library" && pick
              ? { kind: "library", documentId: pick.documentId }
              : { kind: "upload", file: oldUpload.file as File },
        }
      : null;
  const canCompare = !!input && compare.canCompare && !blocked && state.phase !== "running";

  const handleCompare = () => {
    if (!input || !canCompare) return;
    setPickError(null);
    void compare.compare(input);
  };

  const oldName = tab === "library" ? (pick?.fileName ?? "") : safeFileName(oldUpload.file?.name ?? "");
  const newName = safeFileName(newUpload.file?.name ?? "");

  // ---- focus: a result or panel takes it, Cancel gives it back to Compare, "Compare other files" to the picker
  const resultHeading = useRef<HTMLHeadingElement | null>(null);
  const panelHeading = useRef<HTMLHeadingElement | null>(null);
  const previousPhase = useRef(state.phase);
  const previousPanel = useRef(false);
  // Set when a run ends; the focus lands once the page has settled on the pickers (a refused file resets first).
  const focusAfterRun = useRef(false);
  const showsPanel = !!failure && PANEL_KINDS.includes(failure.kind);
  useEffect(() => {
    const before = previousPhase.current;
    const hadPanel = previousPanel.current;
    previousPhase.current = state.phase;
    previousPanel.current = showsPanel;
    if (before === "running" && state.phase !== "running") focusAfterRun.current = true;

    if (state.phase === "result") {
      focusAfterRun.current = false;
      resultHeading.current?.focus();
    } else if (showsPanel) {
      focusAfterRun.current = false;
      panelHeading.current?.focus();
    } else if (focusAfterRun.current && failure?.kind !== "file") {
      // Cancel, a busy answer, a refusal under a file: Cancel is gone, so focus goes back to Compare (or, when
      // Compare is off, to the first step's heading) instead of dropping to the top of the document.
      focusAfterRun.current = false;
      const compareButton = document.getElementById("compare_versions_btn") as HTMLButtonElement | null;
      if (compareButton && !compareButton.disabled) compareButton.focus();
      else document.getElementById("compare-old-title")?.focus();
    } else if (!focusAfterRun.current && state.phase === "idle" && (before === "result" || hadPanel)) {
      // "Compare other files" / "Back to the files": the picker, where the user changes a choice.
      document.getElementById("compare-old-title")?.focus();
    }
  }, [state.phase, showsPanel, failure]);

  const backToSearch = () => history.push(viewpaths.contractSearchViewPath);
  const reset = () => compare.reset();
  const uploadInstead = () => {
    compare.reset();
    selectTab("upload");
  };

  if (!hasFetchPermission) {
    return <Unauthorized permissionDenied />;
  }
  if (failure?.kind === "unauthorized") {
    return <Unauthorized permissionDenied />;
  }

  // ---- the views
  // The header is the same in every view; only the picker passes the Compare button, which sits on the right of the
  // title (Jay, 2026-11-04). In the DOM it follows the title and comes before the subtitle and the steps; where the two
  // do not fit one row it wraps under the title, still on the right.
  const heading = (action?: ReactNode) => (
    <Box sx={styles.header}>
      <Box sx={styles.titleRow}>
        <Box component="h1" sx={styles.title}>
          {page.title}
        </Box>
        {action}
      </Box>
      <Box component="p" sx={styles.subtitle}>
        {page.subtitle}
      </Box>
    </Box>
  );

  const retryActions: PanelAction[] = [
    { label: page.retry, onClick: compare.retry, icon: <RefreshGlyph sx={BUTTON_ICON} />, primary: true },
    { label: page.backToFiles, onClick: reset, icon: <ArrowLeftGlyph sx={BUTTON_ICON} /> },
  ];

  const failurePanel = (f: CompareFailure): ReactNode => {
    switch (f.kind) {
      case "limit":
        return (
          <OutcomePanel
            alert
            tone="warning"
            icon={<HourglassGlyph sx={PANEL_ICON} />}
            title={f.message}
            actions={[{ label: page.backToSearch, onClick: backToSearch, icon: <ArrowLeftGlyph sx={BUTTON_ICON} />, primary: true }]}
            headingRef={panelHeading}
          />
        );
      case "switchedOff":
        return (
          <OutcomePanel
            alert
            tone="neutral"
            icon={<LockGlyph sx={PANEL_ICON} />}
            title={f.message}
            actions={[]}
            headingRef={panelHeading}
          />
        );
      case "timedOut":
        return (
          <OutcomePanel
            alert
            tone="neutral"
            icon={<HourglassGlyph sx={PANEL_ICON} />}
            title={f.message}
            text={page.tookTooLongText}
            actions={retryActions}
            headingRef={panelHeading}
          />
        );
      case "reload":
        return (
          <OutcomePanel
            alert
            tone="neutral"
            icon={<AlertTriangleGlyph sx={PANEL_ICON} />}
            title={f.message}
            actions={[{ label: page.backToFiles, onClick: reset, icon: <ArrowLeftGlyph sx={BUTTON_ICON} /> }]}
            headingRef={panelHeading}
          />
        );
      default:
        return (
          <OutcomePanel
            alert
            tone="neutral"
            icon={<AlertTriangleGlyph sx={PANEL_ICON} />}
            title={f.kind === "unavailable" ? f.message : ""}
            actions={retryActions}
            headingRef={panelHeading}
          />
        );
    }
  };

  const message: CompareMessage | null =
    failure?.kind === "general"
      ? { tone: "danger", text: failure.message }
      : failure?.kind === "busy"
        ? { tone: "warning", text: countdownMessage(failure, compare.secondsLeft) }
        : null;

  const pickers = (
    <Box sx={styles.steps}>
      <OldVersionPicker
        tab={tab}
        onTab={selectTab}
        library={library}
        pick={pick}
        pickFromSearch={pickFromSearch}
        onPick={onPick}
        pickError={pickError}
        upload={oldUpload}
        onUploadFile={onUploadFile("old")}
        onUploadRemove={onUploadRemove("old")}
        disabled={blocked}
        disabledMessage={disabledMessage}
        isPhone={isPhone}
        bypass={guard.bypass}
      />
      <Box component="section" sx={styles.stepCard} aria-labelledby="compare-new-title">
        <Box sx={styles.stepHead}>
          <Box sx={styles.stepBadge} aria-hidden="true">
            2
          </Box>
          <Box>
            <Box component="h2" id="compare-new-title" sx={styles.stepTitle}>
              {page.stepNewTitle}
            </Box>
            <Box component="p" sx={styles.stepHint}>
              {page.stepNewHint}
            </Box>
          </Box>
        </Box>
        <UploadZone
          side="new"
          file={newUpload.file}
          problem={newUpload.problem}
          onFile={onUploadFile("new")}
          onRemove={onUploadRemove("new")}
          disabled={blocked}
          disabledMessage={disabledMessage}
        />
      </Box>
    </Box>
  );

  const view = (() => {
    if (state.phase === "running") {
      return (
        <>
          {heading()}
          <CompareProgress oldName={oldName} newName={newName} onCancel={compare.cancel} />
        </>
      );
    }
    if (state.phase === "result") {
      return (
        <ResultView
          result={state.result}
          oldName={oldName}
          newName={newName}
          onReset={reset}
          onUploadInstead={uploadInstead}
          onRetry={compare.retry}
          headingRef={resultHeading}
        />
      );
    }
    if (failure && showsPanel) {
      return (
        <>
          {heading()}
          {failurePanel(failure)}
        </>
      );
    }
    return (
      <>
        {heading(
          <CompareButton
            canCompare={canCompare}
            busySeconds={failure?.kind === "busy" ? compare.secondsLeft : 0}
            onCompare={handleCompare}
          />,
        )}
        <CompareNotice message={message} announce={busyAnnouncement} />
        <FeatureBanner state={gateState} featureName={page.planFeature} variant="compact" />
        {pickers}
      </>
    );
  })();

  return (
    <CenteredAuthenticatedLayout>
      <Box sx={styles.page}>{view}</Box>
      <ConfirmModal
        open={guard.pending}
        onClose={guard.stay}
        title={page.leaveTitle}
        message={page.leaveText}
        confirmLabel={page.leaveConfirm}
        cancelLabel={page.leaveStay}
        onConfirm={guard.confirmLeave}
        onCancel={guard.stay}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default ContractCompareComponent;
